<template>
  <form :class="style['form-wrap']" novalidate="true" @submit.prevent="updateContact">
    <text-input
      v-if="showFullName"
      v-model="contactForm.name"
      :icon="iconPerson"
      :placeholder="$t('contact_form.full_name')"
      :error="errors.name"
      @input="validateName"
    />
    <text-input
      v-if="showFamilyName"
      v-model="contactForm.familyName"
      :icon="iconPerson"
      :placeholder="$t('contact_form.family_name')"
      :error="errors.familyName"
      @input="validateFamilyName"
    />
    <text-input
      v-if="showFamilyName"
      v-model="contactForm.firstName"
      :icon="iconPerson"
      :placeholder="$t('contact_form.first_name')"
      :error="errors.firstName"
      @input="validateFirstName"
    />
    <text-input
      v-model="contactForm.countryCode"
      :class="style['phone-left']"
      :placeholder="$t('icc')"
    />
    <!-- keep this here for when we add keyboard shortcut for the long list of codes -->
    <!-- :read-only='true' -->
    <!-- @click='openCountryCodeForm' -->
    <text-input
      v-model="contactForm.phone"
      :class="style['phone-right']"
      :icon="iconPhone"
      :placeholder="$t('phone_number')"
      :max-length="15"
      :error="errors.phone"
      @input="validateTel"
    />
    <text-input
      v-model="contactForm.email"
      type="email"
      :icon="iconMail"
      :placeholder="$t('email')"
      :error="errors.email"
      :disabled="emailDisabled"
      @input="validateEmail"
    />
    <text-input
      v-if="confirmEmail"
      v-model="contactForm.emailConfirmation"
      type="email"
      :icon="iconMail"
      :placeholder="$t('email_confirmation')"
      :error="errors.emailConfirmation"
      :disabled="emailDisabled"
      @input="validateEmail"
    />
    <text-input
      v-if="isRoom"
      v-model="contactForm.ccEmail"
      type="email"
      :icon="iconMail"
      :placeholder="$t('contact_form.cc_email')"
      :required="false"
      :error="errors.ccEmail"
      @input="validateCCEmail"
    />
    <p v-if="agreementText" :class="style['agreement-text']" v-html="agreementText" />
    <div>
      <button v-theme :class="style['btn-pink']" :disabled="!validateForm()" type="submit">
        {{ update }}
      </button>
    </div>
  </form>
</template>

<script>
import TextInput from 'UtilityComponents/TextInput.vue'
import style from './scss/_pages.scss'
import { iconPerson, iconPhone, iconMail } from 'javascript/Constants'
import { EventBus } from 'ChatWidget/EventBus'
import { Chat } from 'src/apis'
import {
  onlyEnJp,
  validateTelFormat,
  validateEmailFormat,
  validateEmailConfirmation
} from './validation'
import { formatChatMessage } from 'Utilities'
import { useUserStore } from 'Store/user'
import { useDetailsPageStore } from 'Store/details-page'
import { mapState } from 'pinia'
import { useFormStore } from 'Store/form'

export default {
  components: {
    TextInput
  },

  setup() {
    return {
      userStore: useUserStore(),
      detailsPageStore: useDetailsPageStore(),
      formStore: useFormStore()
    }
  },

  data() {
    return {
      iconPerson,
      iconPhone,
      iconMail,
      style,
      isRentalCar: false,
      errors: {
        name: null,
        phone: null,
        email: null,
        ccEmail: null
      },
      formTouched: false
    }
  },
  computed: {
    ...mapState(useFormStore, {
      contactForm: 'contactForm'
    }),

    isKnownUser() {
      return this.userStore.isKnownUser
    },

    chat() {
      return this.detailsPageStore.chat
    },
    update() {
      const state = this.chat.message_rich.state
      if (this.chat.message_rich.submit_btn_text) {
        return this.chat.message_rich.submit_btn_text
      } else if (state === 'get_contact') {
        return this.$t('contact_form.submit')
      } else if (state === 'modify_booking') {
        return this.$t('confirm_form.update_contact')
      } else {
        this.isRentalCar = true
        return this.$t('next')
      }
    },
    isRoom() {
      // TODO: update this when we figure out what to do with cc-email
      // return this.chat.message_rich.show_cc_email
      return false
    },
    agreementText() {
      const text = this.chat.message_rich.agree_description
      return text && formatChatMessage(text)
    },
    countryCode() {
      const locale = this.$i18n.locale
      if (locale === 'ja') {
        return '+81'
      } else if (locale === 'zh_Hant') {
        return '+886'
      } else if (locale === 'ko') {
        return '+82'
      } else {
        return null
      }
    },
    showFullName() {
      return !this.showFamilyName
    },
    showFamilyName() {
      return this.chat.message_rich.show_family_name
    },
    confirmEmail() {
      return this.chat.message_rich.confirm_email
    },
    emailDisabled() {
      return this.isKnownUser && !this.chat.message_rich.enable_email
    }
  },
  created() {
    if (!this.contactForm.countryCode) {
      this.formStore.assignFieldValue({ countryCode: this.countryCode })
    }
  },
  methods: {
    validateForm() {
      return (
        (this.contactForm.name || (this.contactForm.familyName && this.contactForm.firstName)) &&
        this.contactForm.phone &&
        this.contactForm.countryCode &&
        this.contactForm.email &&
        (!this.confirmEmail || this.contactForm.emailConfirmation) &&
        !this.errors.name &&
        !this.errors.familyName &&
        !this.errors.firstName &&
        !this.errors.phone &&
        !this.errors.email &&
        !this.errors.emailConfirmation &&
        !this.errors.ccEmail
      )
    },
    composeReply() {
      const replyArray = []
      if (this.contactForm.familyName) {
        replyArray.push(`${this.$t('contact_form.family_name')}: ${this.contactForm.familyName}`)
        replyArray.push(`${this.$t('contact_form.first_name')}: ${this.contactForm.firstName}`)
      } else {
        replyArray.push(`${this.$t('contact_form.full_name')}: ${this.contactForm.name}`)
      }
      const phone = `+${this.contactForm.countryCode}-${this.contactForm.phone}`
      replyArray.push(`${this.$t('phone_number')}: ${phone}`)
      replyArray.push(`${this.$t('email')}: ${this.contactForm.email}`)
      if (this.contactForm.ccEmail) {
        replyArray.push(`${this.$t('contact_form.cc_email')} ${this.contactForm.ccEmail}`)
      }
      return replyArray.join('\n')
    },
    updateContact() {
      this.contactForm.phone = this.contactForm.phone.replace(/[+-\s]/g, '')
      this.contactForm.countryCode = this.contactForm.countryCode.replace(/[+\s]/g, '')
      this.formTouched = true
      this.validateTel(this.contactForm.phone)
      this.validateEmail(this.contactForm.email)
      this.validateCCEmail(this.contactForm.ccEmail)
      if (!this.validateForm()) return
      const payload = this.contactForm
      payload.page = 'contact_form'
      Chat.sendMessage(this.composeReply(), { messageAction: JSON.stringify(payload) })
      this.detailsPageStore.closeDetailsPageShown()
      EventBus.$emit('pageDisabledActionWidget')
    },
    validateName(value) {
      this.errors.name = onlyEnJp(value, this.isRentalCar)
    },
    validateFamilyName(value) {
      this.errors.family_name = onlyEnJp(value, this.isRentalCar)
    },
    validateFirstName(value) {
      this.errors.first_name = onlyEnJp(value, this.isRentalCar)
    },
    validateTel(value) {
      this.errors.phone = validateTelFormat(value)
      if (this.contactForm.phone && this.contactForm.phone.length < 6 && this.formTouched) {
        this.errors.phone = this.$t('contact_form.error.tel_length_error')
      }
    },
    validateEmail(value) {
      if (this.formTouched) {
        this.errors.email = validateEmailFormat(this.contactForm.email)
        if (this.confirmEmail) {
          this.errors.emailConfirmation = validateEmailConfirmation(
            this.contactForm.email,
            this.contactForm.emailConfirmation
          )
        }
      }
    },
    validateCCEmail(value) {
      if (this.formTouched) {
        this.errors.ccEmail = validateEmailFormat(this.contactForm.ccEmail)
      }
    },
    openCountryCodeForm() {
      this.detailsPageStore.setDetailsPageShown('CountryCodeForm')
    }
  }
}
</script>
